import { Pin, PinOff, ThumbsDown, ThumbsUp } from 'lucide-react'
import { ContextMenu } from '@highlight/ui'
import { CopyBraintrustLinkMenuItem } from '../../copy-braintrust-link-menu-item'
import { useConversationBraintrustLink } from '../../use-conversation-braintrust-link'
import { CELL_CONTEXT_MENU_TRIGGER, CellActionsMenu } from '../../cell-actions'
import { useReminderCellActions } from '../../reminder-row-actions'
import type { ReminderItem } from '../../../hooks/use-reminders'
import { useReminderActions } from '../../../hooks/use-reminders'
import { DraftActionMenuItem } from './draft-action-menu-item'
import { isPinnedReminder } from '../utils/pinned'

interface ReminderContextMenuItemsProps {
  item: ReminderItem
  resolved: boolean
  lingering?: boolean
  /** Focus the title for in-place edit — see `useReminderCellActions`. */
  onEdit?: () => void
  onSaving?: () => void
  onFeedback: (type: 'positive' | 'negative') => void
  onPopout?: () => void
}

/**
 * The right-click menu for a reminder: the same moves its hover cluster offers,
 * then the ones with no cluster counterpart. Rating only appears for reminders
 * an assistant message produced — there's nothing to rate on one the user wrote.
 */
export function ReminderContextMenuItems({
  item,
  resolved,
  lingering,
  onEdit,
  onSaving,
  onFeedback,
  onPopout,
}: ReminderContextMenuItemsProps) {
  const { setPinned } = useReminderActions()
  const actions = useReminderCellActions({ item, onEdit, lingering, onSaving, trigger: CELL_CONTEXT_MENU_TRIGGER })
  const pinned = isPinnedReminder(item)

  return (
    <CellActionsMenu actions={actions} onPopout={onPopout}>
      <DraftActionMenuItem item={item} />
      {!resolved && (
        <ContextMenu.Item onClick={() => setPinned(item.id, !pinned)}>
          {pinned ? <PinOff /> : <Pin />}
          <span>{pinned ? 'Unpin' : 'Pin'}</span>
        </ContextMenu.Item>
      )}
      {item.triggeringLlmMessageId && (
        <>
          <ContextMenu.Item onClick={() => onFeedback('positive')}>
            <ThumbsUp />
            <span>Helpful</span>
          </ContextMenu.Item>
          <ContextMenu.Item onClick={() => onFeedback('negative')}>
            <ThumbsDown />
            <span>Not helpful</span>
          </ContextMenu.Item>
          {item.conversationId && (
            <ReminderBraintrustLinkMenuItem
              conversationId={item.conversationId}
              messageId={item.triggeringLlmMessageId}
            />
          )}
        </>
      )}
    </CellActionsMenu>
  )
}

function ReminderBraintrustLinkMenuItem({ conversationId, messageId }: { conversationId: string; messageId: string }) {
  const link = useConversationBraintrustLink(conversationId, messageId)
  return <CopyBraintrustLinkMenuItem link={link} />
}
